import {
  // inferComponentNameFromFileName,
  // isInLoopOrArrayMethod,
  // createContext,
  // generateContextHash,
  hashString,
} from './util';

// 记录已经使用过的 key，防止重复
const usedKeysMap = new Map();
// 全局计数器用来确保唯一性
let keyCounter = 0;

// 创建一个映射来存储组件实例和它们的唯一 ID
const componentInstanceMap = new WeakMap();
let componentCounter = 0;

/**
 * 获取元素类型标识符
 * @param {*} type 元素类型
 * @returns {string} 类型标识符
 */
function getTypeIdentifier(type) {
  // 处理不同类型的元素
  if (typeof type === 'string') {
    // 原生 DOM 元素
    return type;
  } else if (type === null || type === undefined) {
    // 处理 Fragment 或无效类型
    return 'fragment';
  } else if (typeof type === 'symbol') {
    // 处理 React 特殊元素类型
    return type.toString().replace(/Symbol\(|\)/g, '');
  } else if (typeof type === 'function') {
    // 函数组件或类组件
    return type.displayName || type.name || 'component';
  } else if (typeof type === 'object') {
    // React.memo, React.forwardRef 等高阶组件
    if (type.$$typeof && type.render) {
      return type.displayName || type.name || 'memo';
    }
    return 'object';
  }

  // 默认情况
  return 'unknown';
}

/**
 * 清理标识符，移除不安全字符
 * @param {*} value 原始值
 * @returns {string} 清理后的标识符
 */
function sanitizeIdentifier(value) {
  // 转换为字符串
  const str = String(value);
  // 移除不安全字符，保留字母、数字、下划线和连字符
  return str.replace(/[^\w-]/g, '_')
    .replace(/^_+|_+$/g, '') // 移除首尾下划线
    .substring(0, 30); // 限制长度
}

/**
 * 对对象进行哈希处理
 * @param {Object} obj 要哈希的对象
 * @returns {string} 哈希结果
 */
function hashObject(obj) {
  try {
    // 使用稳定的 JSON 序列化
    const str = JSON.stringify(obj, Object.keys(obj).sort());
    return hashString(str).substring(0, 8);
  } catch (e) {
    // 如果 JSON 序列化失败，使用备用方法
    return (++keyCounter).toString(36);
  }
}

/**
 * 从 props 中提取可能的唯一标识符
 * @param {Object} props 元素属性
 * @returns {string|null} 提取的标识符或 null
 */
function getPropsIdentifier(props) {
  if (!props) return null;

  // 优先级排序的可能标识符
  const identifierProps = [
    'id',
    'key', // 如果已有 key 但为 null 或 undefined
    'data-testid',
    'name',
    'href',
    'src',
    'title',
    'alt',
    'aria-label'
  ];

  // 尝试使用这些属性作为标识符
  for (const prop of identifierProps) {
    if (props[prop] !== undefined && props[prop] !== null && props[prop] !== '') {
      return sanitizeIdentifier(props[prop]);
    }
  }

  // 尝试使用文本内容作为标识符
  if (typeof props.children === 'string') {
    const textContent = props.children.trim();
    if (textContent) {
      // 限制长度并移除特殊字符
      return sanitizeIdentifier(textContent.slice(0, 15));
    }
  }

  // 尝试使用 props 对象的哈希作为标识符
  try {
    // 过滤掉函数和复杂对象，只保留简单值
    const simpleProps = Object.entries(props)
      .filter(([key, value]) => {
        const type = typeof value;
        return value !== null
          && (type === 'string'
            || type === 'number'
            || type === 'boolean')
          && key !== 'children';
      })
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});

    if (Object.keys(simpleProps).length > 0) {
      return hashObject(simpleProps);
    }
  } catch (e) {
    // 忽略错误，继续使用后备方法
  }

  return null;
}

/**
 * 获取组件实例的唯一 ID
 * @param {*} type 组件类型
 * @returns {string} 实例 ID
 */
function getComponentInstanceId(type) {
  if (typeof type !== 'function') return '';

  if (!componentInstanceMap.has(type)) {
    componentInstanceMap.set(type, (++componentCounter).toString(36));
  }

  return componentInstanceMap.get(type);
}

/**
 * 确保在当前上下文中 key 是唯一的
 * @param {string} key 基础 key
 * @param {string} contextId 上下文 ID
 * @returns {string} 唯一的 key
 */
function ensureUniqueKey(key, contextId) {
  if (!usedKeysMap.has(contextId)) {
    usedKeysMap.set(contextId, new Set([key]));
    return key;
  }

  const usedKeys = usedKeysMap.get(contextId);
  let uniqueKey = key;
  let counter = 0;

  // 如果 key 已存在，添加计数器直到找到唯一的 key
  while (usedKeys.has(uniqueKey)) {
    uniqueKey = `${key}_${(++counter).toString(36)}`;
  }

  usedKeys.add(uniqueKey);
  return uniqueKey;
}

/**
 * 为元素生成稳定且唯一的 key
 * @param {*} type 元素类型
 * @param {Object} props 元素属性
 * @param {string} contextId 当前列表上下文ID
 * @returns {string} 生成的 key
 */
export function generateStableKey(type, props, contextId) {
  // 1. 识别元素类型
  const typeIdentifier = getTypeIdentifier(type);

  // 2. 从 props 中提取可能的唯一标识符
  const propsIdentifier = getPropsIdentifier(props);

  // 3. 组合基础 key
  const baseKey = propsIdentifier
    ? `${typeIdentifier}_${propsIdentifier}`
    : `${typeIdentifier}_${getComponentInstanceId(type)}`;

  // 4. 结合上下文 ID 生成完整 key
  const fullKey = `auto_${baseKey}_${contextId}`;

  // 5. 确保在当前列表上下文中 key 是唯一的
  return ensureUniqueKey(fullKey, contextId);
}

// 生成稳定且唯一的 key
function generateStableKey1(type, props, contextId = '') {
  // 1. 识别元素类型
  const typeIdentifier = getTypeIdentifier(type);

  // 如果有可能的唯一标识符（如 id 或 data-testid），优先使用它们
  const possibleIdentifiers = props ? [
    props.id,
    props['data-testid'],
    props.name,
    props.title,
    props.alt,
    // props.href,
    // props.src,
  ].filter(Boolean) : [];

  let baseKey = '';
  if (possibleIdentifiers.length > 0) {
    baseKey = possibleIdentifiers.join('_');
  } else {
    // 如果是已知的组件实例，使用映射的唯一 ID
    if (typeof type === 'function' && !componentInstanceMap.has(type)) {
      componentInstanceMap.set(type, ++componentCounter);
    }
    const instanceId = typeof type === 'function' ? componentInstanceMap.get(type) : '';
    baseKey = `${typeIdentifier}_${instanceId || ''}${keyCounter++}`;
  }

  let key = `auto_${contextId ? `${contextId}_` : ''}${baseKey}`;

  // 确保在相同上下文中 key 是唯一的
  if (usedKeysMap.has(contextId)) {
    const usedKeys = usedKeysMap.get(contextId);
    if (usedKeys.has(key)) {
      key = `${key}_${keyCounter++}`;
      usedKeys.add(key);
    } else {
      usedKeys.add(key);
    }
  } else {
    usedKeysMap.set(contextId, new Set([key]));
  }

  return key;
}
